import { useEffect, useState } from 'react'

import { Card } from '@taro/cards'
import { Button, Stack, Text } from '@taro/design'
import { InkReveal } from '@taro/ink/react'
import { haptic } from '@taro/tg'

import type { Reading, ReadingCard, SpreadPosition } from '../../api'
import styles from './reading.module.css'

interface Props {
  reading: Reading
  onVerify: () => void
  onAgain: () => void
}

/** Пауза между открытием соседних карт. */
const DEAL_MS = 520

/**
 * Готовый расклад.
 *
 * Карты открываются по одной, в том порядке, в каком человек их выбирал.
 * Разом — быстрее, но расклад перестаёт быть событием: смысл в том, чтобы
 * каждая карта успела прозвучать отдельно.
 */
export function Table({ reading, onVerify, onAgain }: Props) {
  const total = reading.cards.length
  const [opened, setOpened] = useState(0)

  useEffect(() => {
    if (opened >= total) return
    const t = setTimeout(() => {
      setOpened((n) => n + 1)
      haptic('tap')
    }, DEAL_MS)
    return () => clearTimeout(t)
  }, [opened, total])

  // Порядок открытия — порядок выбора, а не порядок мест: человек помнит,
  // какую карту тянул первой.
  const byDraw = [...reading.cards].sort((a, b) => a.drawOrder - b.drawOrder)
  const isOpen = (card: ReadingCard) => byDraw.indexOf(card) < opened

  return (
    <Stack gap={5}>
      <Stack gap={1}>
        <Text variant="overline" tone="tertiary">
          {reading.spread.title}
        </Text>
        {reading.question ? (
          <Text variant="title-s" as="h2" measure>
            «{reading.question}»
          </Text>
        ) : (
          <Text variant="title-s" as="h2">
            {reading.spread.subtitle}
          </Text>
        )}
      </Stack>

      <Layout positions={reading.spread.positions} cards={reading.cards} isOpen={isOpen} />

      <Stack gap={5}>
        {reading.cards.map((card) => (
          <InkReveal key={card.position.idx} show={isOpen(card)} salt={card.position.idx}>
            <Stack gap={2} className={styles.meaning}>
              <Text variant="overline" tone="sacred">
                {card.position.title}
              </Text>
              <Text variant="title-s" as="h3">
                {card.name}
                {card.isReversed ? (
                  <span className={styles.reversedMark}> · перевёрнута</span>
                ) : null}
              </Text>
              <Text variant="body-m" measure>
                {card.meaning}
              </Text>
            </Stack>
          </InkReveal>
        ))}
      </Stack>

      <InkReveal show={opened >= total}>
        <Stack gap={3}>
          {/* Толкование целиком появится на этапе 2Б. Пока честно
              сказано, что его нет, — заглушка «скоро» врала бы. */}
          <Text variant="body-s" tone="tertiary" measure>
            Пока это значения карт сами по себе. Связный разбор с учётом мест
            расклада и твоего вопроса появится, когда подключим генерацию.
          </Text>

          <Button variant="ghost" fullWidth onClick={onVerify}>
            Проверить, что расклад честный
          </Button>
          <Button variant="secondary" fullWidth onClick={onAgain}>
            Другой расклад
          </Button>
        </Stack>
      </InkReveal>
    </Stack>
  )
}

/**
 * Раскладка на столе.
 *
 * Координаты приходят с сервера долями габарита, а не пикселями: расклад
 * должен узнаваться и на узком телефоне, и на планшете. Пропорции коробки
 * и размер карты выводятся из самих координат — см. shape().
 */
function Layout({
  positions,
  cards,
  isOpen,
}: {
  positions: SpreadPosition[]
  cards: ReadingCard[]
  isOpen: (c: ReadingCard) => boolean
}) {
  const { ratio, width } = shape(positions)
  const byIdx = new Map(cards.map((c) => [c.position.idx, c]))

  return (
    <div
      className={styles.table}
      style={{ aspectRatio: `1 / ${ratio}`, '--slot-w': `${width * 100}%` } as React.CSSProperties}
    >
      {positions.map((p) => {
        const card = byIdx.get(p.idx)
        const open = card ? isOpen(card) : false

        return (
          <div
            key={p.idx}
            className={styles.slot}
            style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, rotate: `${p.rotate}deg` }}
          >
            <Card
              back="/card-back.webp"
              face={card?.image}
              label={open && card ? `${p.title}: ${card.name}` : p.title}
              revealed={open}
              reversed={card?.isReversed}
              style={{ '--card-w': 'var(--slot-w)' } as React.CSSProperties}
            />
            <span className={styles.slotLabel}>{p.short}</span>
          </div>
        )
      })}
    </div>
  )
}

/** Отношение сторон карты: высота к ширине. */
const CARD_RATIO = 1.6
/** Зазор между картами — доля от ширины карты. */
const GAP = 0.92

/**
 * Пропорции коробки и размер карты — из самих координат.
 *
 * Считать это на сервере было бы неправильно: геометрия расклада — про
 * смысл (что где лежит), а сколько это пикселей — про экран, и знает
 * об экране только фронт.
 */
function shape(positions: SpreadPosition[]): { ratio: number; width: number } {
  // Один расклад из одной карты: размах нулевой, и общая формула дала бы
  // пустую вытянутую коробку.
  if (positions.length === 1) return { ratio: 0.85, width: 0.5 }

  const xs = positions.map((p) => p.x)
  const ys = positions.map((p) => p.y)
  const spanX = Math.max(0.1, Math.max(...xs) - Math.min(...xs))
  const spanY = Math.max(0.1, Math.max(...ys) - Math.min(...ys))

  // Карта выше, чем шире, поэтому вертикальному размаху нужно больше места.
  const ratio = Math.min(1.5, Math.max(0.55, (spanY * CARD_RATIO) / spanX))

  // Самая тесная пара и определяет размер карты. Для каждой пары карта
  // помещается, если она расходится с соседкой хотя бы по одной оси;
  // берём меньшее из требований по всем парам.
  let width = 0.5
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const a = positions[i]
      const b = positions[j]
      if (!a || !b) continue
      const dx = Math.abs(a.x - b.x)
      const dy = Math.abs(a.y - b.y)
      // Совпадающие координаты — это намеренное наложение (в Кельтском
      // кресте вторая карта лежит поперёк первой). Оно размер не ограничивает.
      if (dx < 0.01 && dy < 0.01) continue
      width = Math.min(width, Math.max(dx, (dy * ratio) / CARD_RATIO))
    }
  }

  return { ratio, width: Math.max(0.14, width * GAP) }
}
